import CategoryDescription from 'components/CategoryDescription';
import { DATA_SOURCES } from '@/config/constants';
import EntityBreadcrumbs from '../shared/entity/EntityBreadcrumbs';
import EntityCreators from '../shared/entity/EntityCreators';
import EntityFlavors from '../shared/entity/EntityFlavors';
import EntityHeroCard from '../shared/entity/EntityHeroCard';
import EntityPage from '../shared/entity/EntityPage';
import EntityRelatedTable from '../shared/entity/EntityRelatedTable';
import EntityTabs, { EntityTab } from '../shared/entity/EntityTabs';
import EntityTopPosts from '../shared/entity/EntityTopPosts';
import EntityTopTerms from '../shared/entity/EntityTopTerms';
import { hasFlavorsTab } from '../shared/entityPredicates';
import { getTikTokAndSearchHealthStats } from 'state/utils';
import {
    RelatedBrandsSectionTable,
    RelatedCategoriesSectionTable,
} from 'components/DataVis/RelatedCategoriesTable';
import TikTokBreadCrumbs from 'components/Breadcrumbs/TikTokBreadCrumbs';
import { TikTokChartCard } from '../shared/entity/chartCards';
import { TIKTOK_METADATA_BY_KEY } from 'utils/metrics';
import ToggleWatch from 'components/Button/ToggleWatch';
import { TopHashtagsSection } from 'components/TopKeywordsSection';
import { TopPostsSection } from 'components/Posts';
import { useCurrentCountry } from 'utils/hooks';
import useTikTokEntity from '../shared/entity/useTikTokEntity';
import { useTrackCategoryView } from 'utils/hooks/useTrackEntityView';

const METRICS = [
    TIKTOK_METADATA_BY_KEY.engagement_prc,
    TIKTOK_METADATA_BY_KEY.sentiment_score,
    TIKTOK_METADATA_BY_KEY.total_posts_cnt,
    TIKTOK_METADATA_BY_KEY.partner_paid_partnership_posts_views_prc,
    TIKTOK_METADATA_BY_KEY.eligible_for_commission_posts_views_prc,
];

function TikTokCategoryFivePage({ categoryId, controller, tabs }) {
    const country = useCurrentCountry();
    const page = useTikTokEntity({
        entityType: 'categories',
        entityId: categoryId,
        country,
        controller,
        coverage: 'hint',
    });
    const { details, entityQuery, name, row } = page;

    useTrackCategoryView(DATA_SOURCES.TIKTOK, {
        id: categoryId,
        categoryName: name,
        countryName: country?.name || null,
    });

    const healthStats = getTikTokAndSearchHealthStats({
        healthStatKeys: [
            'average weekly views',
            'paid views',
            'sentiment score',
            'posts',
            'engagement',
            'TikTok shop',
        ],
        tikTokDataPoint: row,
    });

    return (
        <EntityPage
            page={page}
            breadcrumbs={<EntityBreadcrumbs as={TikTokBreadCrumbs} />}
            sourceTabs={tabs}
            title={{ name, isLoading: entityQuery.isLoading }}
            actions={
                <ToggleWatch
                    id={categoryId}
                    data={{ name, type: 'category' }}
                />
            }
            sections={
                <>
                    <EntityTopTerms as={TopHashtagsSection} />
                    <EntityTopPosts as={TopPostsSection} />
                </>
            }
            tabs={
                <EntityTabs>
                    <EntityTab name="Related Categories">
                        <EntityRelatedTable
                            as={RelatedCategoriesSectionTable}
                            subject="categories"
                            icon="affinity"
                            title="Related Categories"
                            exportType="related-hashtag"
                            exportEntity={name || 'trend'}
                            sortParamKey="relatedSort"
                        />
                    </EntityTab>
                    <EntityTab name="Related Brands">
                        <EntityRelatedTable
                            as={RelatedBrandsSectionTable}
                            subject="brands"
                            icon="brand"
                            title="Related Brands"
                            exportType="brand-hashtag"
                            exportEntity={name || 'trend'}
                            sortParamKey="brandSort"
                        />
                    </EntityTab>
                    {hasFlavorsTab(page) && (
                        <EntityTab name="Flavors">
                            <EntityFlavors
                                dataSource="tiktok"
                                exportEntity={name || 'trend'}
                            />
                        </EntityTab>
                    )}
                    <EntityTab name="Creators">
                        <EntityCreators exportEntity={name || 'trend'} />
                    </EntityTab>
                </EntityTabs>
            }
        >
            <div className="flex flex-col gap-3">
                <EntityHeroCard healthStats={healthStats} />
                <CategoryDescription
                    categoryId={categoryId}
                    categoryName={name}
                    flavors={details?.flavors}
                />
                <TikTokChartCard
                    metricList={METRICS}
                    healthStats={healthStats}
                    chart={{ cluster: row?.cluster }}
                />
            </div>
        </EntityPage>
    );
}

TikTokCategoryFivePage.displayName = 'TikTokCategoryFive';

export default TikTokCategoryFivePage;
